import {
  DeleteOutlined,
  DownOutlined,
  PictureOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Space, Spin, message } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import Upload from 'antd/es/upload';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { uploadFile } from '@/services/common';
import { resolveBackendResourceUrl } from '@/utils/backendResource';
import { moveArrayItem } from './imageFieldUtils';

interface MultiImageUploaderProps {
  value?: string[];
  onChange?: (urls: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

const normalizeValue = (value?: string[]): string[] =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean),
    ),
  );

const beforeUpload = (file: RcFile): boolean => {
  const isImage = ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type);
  if (!isImage) {
    message.error('只能上传 PNG 或 JPG 格式的图片');
    return false;
  }

  const isLt5M = file.size / 1024 / 1024 < 5;
  if (!isLt5M) {
    message.error('文件大小不能超过 5MB');
    return false;
  }

  return true;
};

const MultiImageUploader: React.FC<MultiImageUploaderProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '上传图片',
}) => {
  const urls = useMemo(() => normalizeValue(value), [value]);
  const latestUrlsRef = useRef<string[]>(urls);
  const [uploadingCount, setUploadingCount] = useState(0);

  useEffect(() => {
    latestUrlsRef.current = urls;
  }, [urls]);

  const handleDelete = (index: number) => {
    onChange?.(urls.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleMove = (fromIndex: number, toIndex: number) => {
    onChange?.(moveArrayItem(urls, fromIndex, toIndex));
  };

  const handleCustomRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    setUploadingCount((count) => count + 1);
    try {
      const response = await uploadFile(file as File);
      if (response?.code === 200 && response?.data?.url) {
        const next = normalizeValue([
          ...latestUrlsRef.current,
          response.data.url,
        ]);
        onChange?.(next);
        onSuccess?.(response, file);
      } else {
        onError?.(new Error(response?.message || '上传失败'));
      }
    } catch (error: any) {
      onError?.(error);
      message.error(error?.message || '上传失败');
    } finally {
      setUploadingCount((count) => Math.max(0, count - 1));
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {urls.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          }}
        >
          {urls.map((url, index) => {
            const previewUrl = resolveBackendResourceUrl(url);
            return (
              <div
                key={`${url}-${index}`}
                style={{
                  border: '1px solid #f0f0f0',
                  borderRadius: 12,
                  padding: 12,
                  background: '#fff',
                }}
              >
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid #f0f0f0',
                    background: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 12,
                  }}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt={`uploaded-${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <PictureOutlined style={{ color: '#999' }} />
                  )}
                </div>
                <Space wrap size={[8, 8]}>
                  <Button
                    size="small"
                    icon={<UpOutlined />}
                    disabled={disabled || index === 0}
                    onClick={() => handleMove(index, index - 1)}
                  >
                    上移
                  </Button>
                  <Button
                    size="small"
                    icon={<DownOutlined />}
                    disabled={disabled || index === urls.length - 1}
                    onClick={() => handleMove(index, index + 1)}
                  >
                    下移
                  </Button>
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={disabled}
                    onClick={() => handleDelete(index)}
                  >
                    删除
                  </Button>
                </Space>
              </div>
            );
          })}
        </div>
      ) : null}

      <Spin spinning={uploadingCount > 0}>
        <Upload
          name="file"
          accept="image/png,image/jpeg,image/jpg"
          beforeUpload={beforeUpload}
          customRequest={handleCustomRequest}
          showUploadList={false}
          disabled={disabled || uploadingCount > 0}
        >
          <Button
            icon={<PlusOutlined />}
            disabled={disabled || uploadingCount > 0}
          >
            {placeholder}
          </Button>
        </Upload>
      </Spin>
    </Space>
  );
};

export default MultiImageUploader;
